/* One-time removal of the retired SalesOS service worker + its app-shell
 * cache (2026-09).
 *
 * Older builds registered /sw.js, which cached the app shell under
 * `salesos-shell-v1` and could keep serving a stale index.html/bundle. SalesOS
 * no longer registers any service worker (see public/index.html); this runs
 * once per page load, on every host (localhost and production alike — it
 * only ever touches what a SalesOS worker could have left behind, so no
 * environment gating is needed):
 *
 *   - unregisters this origin's registrations whose script is the SalesOS
 *     worker (`/sw.js`) — never a worker some other tool registered;
 *   - deletes ONLY Cache Storage entries named `salesos-shell-*`, leaving
 *     every unrelated cache untouched;
 *   - never reloads or navigates, so it can never cause a reload loop, and
 *     is memoised so repeated calls do nothing further.
 * Every step is best-effort: an unsupported browser or a failing API is
 * silently skipped — the app never depends on this succeeding.
 */
export const SALESOS_SHELL_CACHE_PREFIX = "salesos-shell-";
export const SALESOS_SW_SCRIPT_PATH = "/sw.js";

let inFlight = null;

const scriptPathOf = (registration) => {
  const worker = registration && (registration.active || registration.waiting || registration.installing);
  if (!worker || !worker.scriptURL) return null;
  try {
    return new URL(worker.scriptURL).pathname;
  } catch (e) {
    return null;
  }
};

async function runCleanup(nav, cacheStorage) {
  const result = { unregistered: 0, deletedCaches: [] };
  try {
    if (nav && nav.serviceWorker && typeof nav.serviceWorker.getRegistrations === "function") {
      const registrations = await nav.serviceWorker.getRegistrations();
      for (const reg of registrations || []) {
        if (scriptPathOf(reg) !== SALESOS_SW_SCRIPT_PATH) continue;
        try {
          if (await reg.unregister()) result.unregistered += 1;
        } catch (e) { /* best effort */ }
      }
    }
  } catch (e) { /* best effort */ }
  try {
    if (cacheStorage && typeof cacheStorage.keys === "function") {
      const keys = await cacheStorage.keys();
      for (const key of keys || []) {
        if (typeof key !== "string" || !key.startsWith(SALESOS_SHELL_CACHE_PREFIX)) continue;
        try {
          if (await cacheStorage.delete(key)) result.deletedCaches.push(key);
        } catch (e) { /* best effort */ }
      }
    }
  } catch (e) { /* best effort */ }
  return result;
}

export function cleanupLegacyServiceWorker({
  nav = typeof navigator !== "undefined" ? navigator : undefined,
  cacheStorage = typeof caches !== "undefined" ? caches : undefined,
} = {}) {
  if (!inFlight) inFlight = runCleanup(nav, cacheStorage);
  return inFlight;
}

// Test-only: forget the memoised run so each test starts clean.
export function __resetServiceWorkerCleanupForTests() {
  inFlight = null;
}
